import React, { useState, useEffect, useRef } from 'react';
import Loading from '../Common/Loading';

interface HierarchyItem {
    type: 'unit' | 'chapter' | 'lesson' | 'test' | 'lesson_md' | 'lesson_all';
    text: string;
    code: string;
    desc?: string;
    q_details?: string;
    country?: string;
    test_meta?: string;
    dep_lessons?: string;
}

interface SearchResultGroup {
    dic: string;
    units: {
        unit: string;
        unitCode: string;
        chapters: {
            chapter: string;
            chapterCode: string;
            lessons: { lesson: string; code: string }[];
        }[];
    }[];
}

const LessonBrowser: React.FC = () => {
    const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

    const [dicPath, setDicPath] = useState('Live');
    const [country, setCountry] = useState('Australia');
    const [mathsSearch, setMathsSearch] = useState(false);
    const [englishSearch, setEnglishSearch] = useState(false);
    const [chkLessonCode, setChkLessonCode] = useState(true);
    const [chkLessonName, setChkLessonName] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [searchResults, setSearchResults] = useState<SearchResultGroup[]>([]);
    const [dics, setDics] = useState<string[]>([]);
    const [selectedDic, setSelectedDic] = useState('');
    const [initialDicLoaded, setInitialDicLoaded] = useState(false);
    const [hierarchy, setHierarchy] = useState<HierarchyItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [dicsLoading, setDicsLoading] = useState(false);
    const [highlightedLessons, setHighlightedLessons] = useState<Set<string>>(new Set());
    const [highlightedTests, setHighlightedTests] = useState<Set<string>>(new Set());
    const [allLessonSubject, setAllLessonSubject] = useState('All');
    const [activeLesson, setActiveLesson] = useState<string | null>(null);
    const lessonToTestsMap = useRef<Map<string, string[]>>(new Map());
    const [initialized, setInitialized] = useState(false);

    // FIX: Read URL params with correct casing — VB uses "Country", "Lessonid", "FromUsage", "Dicfilename"
    const params = new URLSearchParams(window.location.search);
    const lessonIdFromUsage = params.get("Lessonid") || params.get("lessonid") || '';
    const fromUsage = params.get("FromUsage") || params.get("fromUsage") || '';
    const dicFromUrl = params.get("Dicfilename") || params.get("dicname") || '';
    const countryFromUrl = params.get("Country") || params.get("country") || '';

    useEffect(() => {
        document.body.style.overflowY = "hidden"
    })

    useEffect(() => {
        const map = new Map<string, string[]>();
        hierarchy.forEach(item => {
            if (item.type === 'test' && item.dep_lessons) {
                item.dep_lessons.split(',').filter(l => l.trim()).forEach(lessonId => {
                    const existing = map.get(lessonId) || [];
                    existing.push(item.code);
                    map.set(lessonId, existing);
                });
            }
        });
        lessonToTestsMap.current = map;
    }, [hierarchy]);

    document.body.style.backgroundColor = "#fff";

    // ---------------------------------------------------------------------
    // STYLES
    // Pixel-matched to provided screenshot: tight spacing, compact rows,
    // left-aligned content starting ~20px from edge. No color/logic changes.
    // ---------------------------------------------------------------------
    const styles = {
        body: { fontFamily: 'Arial', fontSize: '9pt', margin: '0px', width: '100%', boxSizing: 'border-box' as const, height: "100vh" },
        navigator: {
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            backgroundColor: '#7a96df', height: '50px', padding: '0 10px',
            color: 'white', fontSize: '18px'
        },
        navLink: { color: 'white', textDecoration: 'none', fontWeight: 'bold' as const },
        navTitle: { textAlign: 'center' as const, flexGrow: 1, fontSize: '19pt' },
        selectCountry: { marginLeft: '20px', marginTop: '12px', fontSize: '12px' },
        radioGroup: {
            marginTop: '0', marginBottom: '10px',
            display: 'flex', alignItems: 'center', flexWrap: 'wrap' as const, gap: '0 6px',
        },
        radioLabel: {
            display: 'inline-flex', alignItems: 'center', gap: '1px',
            cursor: 'pointer', fontSize: '13px', whiteSpace: 'nowrap' as const
        },
        panelCountry: { display: 'block', marginTop: '0' },
        h3: { marginLeft: '0', fontSize: '17px', color: 'black', marginBottom: '6px', marginTop: '6px' },
        countryRow: {
            marginTop: '0', fontSize: '13px', display: 'flex', alignItems: 'center',
            flexWrap: 'wrap' as const, marginBottom: '8px', gap: '4px'
        },
        dbSearch: { marginTop: '4px', display: 'flex', alignItems: 'center', flexWrap: 'wrap' as const, gap: '0 8px' },
        boldLabel: { fontWeight: 'bold' as const, whiteSpace: 'nowrap' as const, fontSize: '13px' },
        textBox: {
            fontFamily: 'Arial', fontSize: '13px', width: '170px',
            paddingLeft: '4px', height: '20px', border: '1px solid #7f9db9', boxSizing: 'border-box' as const
        },
        btn: { fontFamily: 'Arial', fontSize: '9pt', width: '90px', padding: '2px', cursor: 'pointer' },
        ddl: { fontFamily: 'Arial', fontSize: '13px', width: '200px', height: '24px', marginTop: '10px', boxSizing: 'border-box' as const },
        container: {
            fontFamily: 'Arial, sans-serif',
            fontSize: '14px',
            color: '#333',
            margin: '15px',
            marginLeft: '20px',
            marginTop: '3px',

            height: 'calc(100vh - 250px)',   // adjust 180px as needed
            overflowY: 'auto' as const,
        },
        treeH2: { fontSize: '18px', fontWeight: 'bold' as const, color: '#333366', marginBottom: '12px' },
        treeH3: { margin: '10px 0 10px 0', color: '#0044cc', fontSize: '16px', fontWeight: 'bold' as const },
        treeLi: { margin: '5px 0' },
        treeLink: { color: '#0073e6', textDecoration: 'none', fontSize: '14px', cursor: 'pointer' },
        treeTest: { color: 'darkgreen' },
        checkboxLabel: {
            display: 'inline-flex', alignItems: 'center', gap: '1px',
            cursor: 'pointer', fontSize: '13px'
        }
    };

    // FIX: "All lessons" DIC options mirror VB exactly:
    // VB: ddldics.Items.Add("ALL","ALL"), ("Australia","Australia"), ("North America","Canada")
    // The VALUE for North America is "Canada" — this is what gets sent to DB queries.
    // We store value→label mapping for display in the dropdown.
    const ALL_LESSONS_DICS: { value: string; label: string }[] = [
        { value: 'ALL', label: 'ALL' },
        { value: 'Australia', label: 'Australia' },
        { value: 'Canada', label: 'North America' },   // VB: Text="North America" Value="Canada"
    ];

    // 1. Fetch DIC List
    useEffect(() => {
        if (!initialized) return;
        if (dicPath === 'Search-Questions') {

            saveLessonBrowserState();

            window.location.href = 'SearchQnTypes';

            return;
        }

        // FIX: "All lessons" uses hardcoded options — no API call needed (mirrors VB exactly)
        if (dicPath === 'All lessons') {
            setDics([]);           // not used — we use ALL_LESSONS_DICS directly
            setHierarchy([]);
            setSearchResults([]);
            setHighlightedLessons(new Set());
            setHighlightedTests(new Set());
            // VB auto-selects index 0 = "ALL"
            setSelectedDic('ALL');
            return;
        }

        const fetchDics = async () => {
            setDicsLoading(true);
            setHierarchy([]);
            setSearchResults([]);
            setHighlightedLessons(new Set());
            setHighlightedTests(new Set());
            setSelectedDic("")

            const urlParams = new URLSearchParams({
                dicpath: dicPath,
                country: country,
                maths: mathsSearch.toString(),
                english: englishSearch.toString()
            });

            try {
                const response = await fetch(`${API_BASE_URL}api/lesson_browser_get_dics?${urlParams}`);
                const data = await response.json();
                const dicList: string[] = data.dics || [];
                setDics(dicList);
                // console.log("yashu", dics)

                if (!initialDicLoaded && dicFromUrl && dicList.includes(dicFromUrl)) {
                    setSelectedDic(dicFromUrl);
                    setInitialDicLoaded(true);
                } else {
                    setSelectedDic(prevSelected => {
                        if (prevSelected && dicList.includes(prevSelected))
                            return prevSelected;

                        return '';
                    });
                }
            } catch (err) {
                console.error("Error fetching DICs:", err);
            } finally {
                setDicsLoading(false);
            }
        };

        fetchDics();
    }, [initialized, dicPath, country, mathsSearch, englishSearch]);

    // Scroll to active lesson after hierarchy loads
    useEffect(() => {
        if (!hierarchy.length) return;
        if (fromUsage !== "1") return;
        if (!lessonIdFromUsage) return;

        requestAnimationFrame(() => {
            const el = document.getElementById(`lesson-${lessonIdFromUsage}`);

            if (el) {
                el.scrollIntoView({
                    behavior: "smooth",
                    block: "center"
                });
            }
        });
    }, [hierarchy]);


    // 2. Fetch Hierarchy Tree
    useEffect(() => {
        if (!selectedDic) return;

        const fetchHierarchy = async () => {
            setLoading(true);
            setHighlightedLessons(new Set());
            setHighlightedTests(new Set());

            let effectiveCountry = country;

            if (dicPath === 'All lessons') {

                if (selectedDic === 'ALL') {
                    effectiveCountry = 'All';
                }
                else if (selectedDic === 'Canada') {
                    effectiveCountry = 'Canada';
                }
                else if (selectedDic === 'Australia') {
                    effectiveCountry = 'Australia';
                }
            }
            const urlParams = new URLSearchParams({
                dicname: selectedDic,
                country: effectiveCountry,
                dicpath: dicPath,
                subject: allLessonSubject
            });

            try {
                const response = await fetch(`${API_BASE_URL}api/lesson_browser_get_hierarchy?${urlParams}`);
                const data = await response.json();

                // console.log("Selected Country:", country);
                // console.log("Hierarchy:", data.hierarchy);
                setHierarchy(data.hierarchy || []);
            } catch (err) {
                console.error("Error fetching Hierarchy:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchHierarchy();
    }, [selectedDic, allLessonSubject]);

    useEffect(() => {

        const savedState =
            sessionStorage.getItem("LessonBrowserState");

        if (savedState) {

            const state = JSON.parse(savedState);

            sessionStorage.removeItem("LessonBrowserState");

            const restoredDicPath = state.dicPath === 'Search-Questions'
                ? 'Live'
                : (state.dicPath || "Live");

            setDicPath(restoredDicPath);
            setCountry(state.country || "Australia");
            setMathsSearch(state.mathsSearch || false);
            setEnglishSearch(state.englishSearch || false);
            setChkLessonCode(state.chkLessonCode ?? true);
            setChkLessonName(state.chkLessonName ?? false);
            setSearchText(state.searchText || "");

            if (state.country === "MD") {
                setSelectedDic(state.selectedCurriculum || "");
            } else {
                setSelectedDic(state.selectedDic || "");
            }

            setAllLessonSubject(state.allLessonSubject || "All");
            setActiveLesson(state.activeLesson || null);

            // ADD THIS
            setInitialized(true);

            return;
        }

        if (dicFromUrl) {
            setSelectedDic(dicFromUrl);
        }

        if (countryFromUrl) {
            setCountry(countryFromUrl);
        }

        setInitialized(true);

        // if (fromUsage === "1" && lessonIdFromUsage)
        //     setActiveLesson(lessonIdFromUsage);

        // ADD THIS
        // setInitialized(true);

    }, []);

    const handleSearch = async (e: React.MouseEvent) => {
        e.preventDefault();
        if (!searchText) return;

        setLoading(true);
        setHierarchy([]);
        setHighlightedLessons(new Set());
        setHighlightedTests(new Set());

        try {
            const response = await fetch(`${API_BASE_URL}api/lesson_browser_search`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    term: searchText,
                    country,
                    chkLessonCode,
                    chkLessonName,
                    maths: mathsSearch,
                    english: englishSearch
                })
            });
            const data = await response.json();
            setSearchResults(data.results || []);
        } catch (err) {
            console.error("Search error:", err);
        } finally {
            setLoading(false);
        }
    };

    // FIX: mirrors VB URL building — MD uses mdcode=selectedDic, EC uses eccode=selectedDic
    const buildCodeParam = (): string => {
        if (country === 'MD' && selectedDic) return `&mdcode=${selectedDic}`;
        if (country === 'EC' && selectedDic) return `&eccode=${selectedDic}`;
        return '';
    };

    const saveLessonBrowserState = () => {
        sessionStorage.setItem(
            "LessonBrowserState",
            JSON.stringify({
                dicPath,
                country,
                mathsSearch,
                englishSearch,
                chkLessonCode,
                chkLessonName,
                searchText,
                selectedDic,
                selectedCurriculum: selectedDic,
                allLessonSubject,
                activeLesson,
                scrollY: window.scrollY
            })
        );
    };

    const handleLessonLink = (code: string, countryOverride?: string) => {
        const c = countryOverride || (country === "North America" ? "Canada" : country);
        const codeParam = buildCodeParam();

        const cleanCode = code.replace(/\s*in_Uni$/i, "").replace(/in_Uni$/i, "");

        const url =
            `LessonViewer?Country=${c}` +
            `&Lessonid=${encodeURIComponent(cleanCode)}` +
            `&Dicfilename=${encodeURIComponent(selectedDic)}` +
            `${codeParam}` +
            `&CalledFrom=LessonBrowser`;

        saveLessonBrowserState();
        window.location.href = url;
    };

    const handleTestClick = (code: string, depLessons?: string) => {
        const newHL = new Set<string>();
        const newHT = new Set<string>();
        newHT.add(code);
        if (depLessons) {
            depLessons.split(",").filter(id => id.trim()).forEach(id => newHL.add(id.trim()));
        }
        setHighlightedLessons(newHL);
        setHighlightedTests(newHT);
    };

    const handleLessonCheck = (e: React.MouseEvent, code: string) => {
        e.preventDefault();
        e.stopPropagation();
        const newHL = new Set<string>();
        const newHT = new Set<string>();
        newHL.add(code);
        (lessonToTestsMap.current.get(code) || []).forEach(tc => newHT.add(tc));
        setHighlightedLessons(newHL);
        setHighlightedTests(newHT);
    };

    const handleLessonClick = (code: string, countryOverride?: string) => {
        setActiveLesson(code);
        handleLessonLink(code, countryOverride);
    };

    const renderTree = () => {
        if (loading) return <Loading />;
        if (searchResults.length > 0) return renderSearchResults();

        // MD — flat list of lessons
        if (country === 'MD' || country === 'EC') {
            console.log("Rendering MD/EC", hierarchy);
            // if (country === 'MD') {
            return (
                <div style={styles.container}>
                    <h2 style={styles.treeH2}>{selectedDic} ({country})</h2>
                    <ul style={{ paddingLeft: '20px', margin: 0 }}>
                        {hierarchy.map((item, idx) => (
                            <li key={idx} style={styles.treeLi}>
                                <a
                                    style={styles.treeLink}
                                    onClick={() => { setActiveLesson(item.code); handleLessonLink(item.code, item.country); }}
                                    onMouseOver={e => e.currentTarget.style.textDecoration = 'underline'}
                                    onMouseOut={e => e.currentTarget.style.textDecoration = 'none'}
                                >
                                    {item.text} ({item.code})
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            );
        }

        const elements: React.ReactNode[] = [];
        let unitList: React.ReactNode[] = [];
        let chapterList: React.ReactNode[] = [];
        let currentUnit: React.ReactNode | null = null;
        let currentChapter: React.ReactNode | null = null;

        // FIX: heading label — VB uses DicCode + country label
        // For "All lessons" the VB shows no h2 in the DIC loop (each lesson is flat)
        if (dicPath !== 'All lessons' && selectedDic) {
            const displayCountry = country === 'Canada' ? 'North America' : country;
            elements.push(<h2 key="h2" style={styles.treeH2}>{selectedDic} ({displayCountry})</h2>);
        }

        hierarchy.forEach((item, idx) => {
            if (item.type === 'unit') {
                if (currentUnit) {
                    if (currentChapter) {
                        unitList.push(
                            <li key={`cc-${idx}`}>
                                {currentChapter}
                                <ul style={{ listStyleType: 'circle', paddingLeft: '24px' }}>{chapterList}</ul>
                            </li>
                        );
                        chapterList = []; currentChapter = null;
                    }
                    elements.push(
                        <div key={`uc-${idx}`}>
                            {currentUnit}
                            <ul style={{ listStyleType: 'disc', paddingLeft: '24px' }}>{unitList}</ul>
                        </div>
                    );
                    unitList = [];
                }
                currentUnit = <h3 style={styles.treeH3}>&#8226; {item.text}</h3>;

            } else if (item.type === 'chapter') {
                if (currentChapter) {
                    unitList.push(
                        <li key={`cc-${idx}`}>
                            {currentChapter}
                            <ul style={{ listStyleType: 'circle', marginTop: "10px", paddingLeft: '24px' }}>{chapterList}</ul>
                        </li>
                    );
                    chapterList = [];
                }
                currentChapter = <b style={{ fontSize: "15px" }}>{item.text}</b>;

            } else if (item.type === 'lesson' || item.type === 'lesson_md' || item.type === 'lesson_all') {
                const isHighlighted = highlightedLessons.has(item.code);
                const cleanItemCode = item.code.replace(/(in_Uni|_Uni)$/i, "");

                const isFromUsage =
                    fromUsage === "1" &&
                    lessonIdFromUsage &&
                    cleanItemCode.toLowerCase() === lessonIdFromUsage.toLowerCase();

                let lessonName = item.text;
                let lessonCode = '';
                let lessonDesc = '';

                if (item.text.includes('--')) {
                    const fullMatch = item.text.match(
                        /^(.*?)\s*\((.*?)\s*--\s*(.*?)\)$/
                    );
                    console.log(item.text);

                    if (fullMatch) {
                        lessonName = fullMatch[1].trim();
                        lessonCode = fullMatch[2].trim();
                        lessonDesc = fullMatch[3].trim();
                    }
                } else {
                    const simpleMatch = item.text.match(
                        /^(.*?)\s*\((.*?)\)$/
                    );

                    if (simpleMatch) {
                        lessonName = simpleMatch[1].trim();
                        lessonCode = simpleMatch[2].trim();
                    }
                }

                const uniMatch = lessonCode.match(/^(.*?)(\s*in_Uni)$/i);

                let normalCode = lessonCode;
                let uniSuffix = "";

                if (lessonCode.endsWith(" in_Uni")) {
                    normalCode = lessonCode.replace(" in_Uni", "");
                    uniSuffix = "in_Uni";
                }
                const lessonNode = (
                    <li
                        key={idx}
                        id={`lesson-${item.code}`}
                        style={{
                            ...styles.treeLi,
                            // FIX: "All lessons" uses list-item with circle bullet (VB <li style='list-style-type:circle'>)
                            display: dicPath === 'All lessons' ? 'list-item' : 'flex',
                            alignItems: dicPath === 'All lessons' ? undefined : 'center',
                            flexWrap: dicPath === 'All lessons' ? undefined : 'wrap',
                            listStyleType: dicPath === 'All lessons' ? 'circle' : 'none',
                            marginLeft: dicPath === 'All lessons' ? '20px' : undefined,
                            backgroundColor: isFromUsage ? 'yellow' : (isHighlighted ? '#d4f0d4' : ''),
                            color: isFromUsage ? 'red' : 'black'
                        }}
                    >
                        <a
                            data-code={item.code}
                            style={{
                                textDecoration: 'none',
                                cursor: 'pointer'
                            }}
                            onClick={() => handleLessonClick(item.code, item.country)}
                        >
                            <span style={{ color: '#0066ff' }}>
                                {lessonName}
                            </span>

                            {lessonCode && (
                                <>
                                    <span style={{ color: '#0066ff' }}>
                                        {" "}({normalCode})
                                    </span>

                                    {uniSuffix && (
                                        <span style={{ color: '#ff9900' }}>
                                            {" "}{uniSuffix}
                                        </span>
                                    )}
                                </>
                            )}

                            {lessonDesc && (
                                <span
                                    style={{
                                        color: '#000',
                                        fontStyle: 'italic',
                                        fontSize: "12px"
                                    }}
                                >
                                    {' '}-- {lessonDesc}
                                </span>
                            )}
                        </a>

                        {item.q_details && (
                            <span
                                style={{ marginLeft: '5px' }}
                                dangerouslySetInnerHTML={{
                                    __html: item.q_details
                                }}
                            />
                        )}
                        {/* FIX: invisible checkbox for highlight-by-lesson interaction — only for non-all-lessons */}
                        {dicPath !== 'All lessons' && (
                            <input
                                type="checkbox"
                                style={{ opacity: 0, width: '40px', minWidth: '20px', flex: '1 1 auto', marginLeft: '6px', cursor: 'pointer' }}
                                onClick={e => handleLessonCheck(e, item.code)}
                                readOnly
                            />
                        )}
                    </li>
                );

                if (currentChapter) chapterList.push(lessonNode);
                else if (currentUnit) unitList.push(lessonNode);
                else elements.push(lessonNode);

            } else if (item.type === 'test') {
                const isTestHighlighted = highlightedTests.has(item.code);
                const testNode = (
                    <li
                        key={idx}
                        style={{
                            ...styles.treeLi, ...styles.treeTest,
                            listStyleType: 'square', fontSize: "9pt", cursor: "pointer",
                            backgroundColor: isTestHighlighted ? '#d4f0d4' : ''
                        }}
                        onClick={() => handleTestClick(item.code, item.dep_lessons)}
                    >
                        {item.text}<br />{item.test_meta}
                    </li>
                );
                if (currentChapter) chapterList.push(testNode);
                else if (currentUnit) unitList.push(testNode);
                else elements.push(testNode);
            }
        });

        if (currentChapter) {
            unitList.push(<li key="ce">{currentChapter}<ul style={{ listStyleType: 'square', paddingLeft: '24px' }}>{chapterList}</ul></li>);
        }
        if (currentUnit) {
            elements.push(<div key="ue">{currentUnit}<ul style={{ listStyleType: 'circle', paddingLeft: '24px' }}>{unitList}</ul></div>);
        }

        return <div style={styles.container}>{elements}</div>;
    };

    const renderSearchResults = () => (
        <div style={styles.container}>
            {searchResults.map((grp, i) => (
                <div key={i}>
                    <h2 style={styles.treeH2}>{grp.dic}</h2>
                    {grp.units?.map((unit, ui) => (
                        <div key={ui}>
                            <h3 style={styles.treeH3}>&#8226; Unit {unit.unit} ({unit.unitCode}) </h3>
                            <ul style={{ listStyleType: 'circle', paddingLeft: '24px' }}>
                                {unit.chapters?.map((chap, ci) => (
                                    <li key={ci}>
                                        <b style={{ fontSize: "15px" }}>{chap.chapter} ({chap.chapterCode})</b>
                                        <ul style={{ listStyleType: 'square', paddingLeft: '24px' }}>
                                            {chap.lessons.map((l, j) => (
                                                <li key={j} style={styles.treeLi}>
                                                    <a
                                                        style={styles.treeLink}
                                                        onClick={() => handleLessonLink(l.code)}
                                                    >
                                                        {(() => {
                                                            const uniMatch = l.code.match(/^(.*?)(in_Uni)$/i);

                                                            if (uniMatch) {
                                                                return (
                                                                    <>
                                                                        {l.lesson} (
                                                                        <span>{uniMatch[1]}</span>
                                                                        <span>)</span>
                                                                        <span style={{ color: '#ff9900' }}> {uniMatch[2]}</span>
                                                                    </>
                                                                );
                                                            }

                                                            return (
                                                                <>
                                                                    {l.lesson} ({l.code})
                                                                </>
                                                            );
                                                        })()}
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );

    const dicPathOptions = [
        { value: 'Live', label: 'Live' },
        { value: 'Drafts', label: 'Drafts' },
        { value: 'Search-Questions', label: 'Search-Questions' },
        { value: 'All lessons', label: 'All lessons' },
    ];

    return (
        <div style={styles.body}>
            {/* Responsive helper rules (no color/logic changes — alignment & breakpoints only) */}
            <style>{`
                @media (max-width: 600px) {
                    .lb-navigator { font-size: 15px !important; }
                    .lb-textbox { width: 100% !important; }
                    .lb-ddl { width: 100% !important; }
                    .lb-btn { width: auto !important; flex: 1 1 auto; }
                }
            `}</style>

            {/* Nav */}
            <div style={styles.navigator} className="lb-navigator">
                <a href="/SysAdmin" style={styles.navLink}>Back</a>
                <div style={styles.navTitle} className="lb-nav-title">Lesson Navigator</div>
                <a href="/" style={styles.navLink}>Logout</a>
            </div>

            <div style={styles.selectCountry}>
                {/* DicPath radios */}
                <div style={styles.radioGroup}>
                    {dicPathOptions.map(({ value, label }) => (
                        <label key={value} style={styles.radioLabel}>
                            <input
                                type="radio" name="dicpath" value={value} style={{ marginTop: "0" }}
                                checked={dicPath === value}
                                onChange={e => {
                                    const v = e.target.value;

                                    setSearchText("");          // <-- Add
                                    setSearchResults([]);       // <-- Already there

                                    setDicPath(v);
                                    setHierarchy([]);
                                    setHighlightedLessons(new Set());
                                    setHighlightedTests(new Set());

                                    if (v === 'All lessons') setAllLessonSubject('All');
                                    // if (v === 'Drafts' && (country === 'MD' || country === 'EC'))
                                    //     setCountry('Australia');

                                    if (v === 'Drafts') {
                                        setCountry('Australia');
                                    }
                                }}
                            />
                            {label}
                        </label>
                    ))}
                </div>

                {/* Country panel — hidden for "All lessons" (mirrors VB pnlCountry display:none) */}
                {dicPath !== 'All lessons' && (
                    <div style={styles.panelCountry}>
                        <h3 style={styles.h3}>Select Country</h3>
                        <div style={styles.countryRow}>
                            {/* FIX: radio labels match VB exactly — "Australia","North America","MD","EC" */}
                            {[
                                { value: 'Australia', label: 'Australia' },
                                { value: 'North America', label: 'North America' },
                                { value: 'MD', label: 'MD' },
                                { value: 'EC', label: 'EC' },
                            ].map(c => (
                                <label
                                    key={c.value}
                                    style={{
                                        ...styles.radioLabel,
                                        // FIX: Drafts hides MD and EC (mirrors VB display:none)
                                        display: (dicPath === 'Drafts' && (c.value === 'MD' || c.value === 'EC')) ? 'none' : 'inline-flex'
                                        // display: (dicPath === 'Drafts' && (c.value === 'MD')) ? 'none' : 'inline-flex'
                                    }}
                                >
                                    <input
                                        type="radio" name="country" value={c.value} style={{ marginTop: "0" }}
                                        checked={country === c.value}
                                        onChange={e => {
                                            setSearchText("");          // <-- Add
                                            setSelectedDic('');
                                            setHierarchy([]);
                                            setSearchResults([]);
                                            setHighlightedLessons(new Set());
                                            setHighlightedTests(new Set());
                                            setCountry(e.target.value);
                                        }}
                                    />
                                    {c.label}
                                </label>
                            ))}
                        </div>

                        {/* Search controls — hidden for Drafts, MD, EC (mirrors VB dbsearch.Visible) */}
                        {dicPath !== 'Drafts' && country !== 'MD' && country !== 'EC' && (
                            // {dicPath !== 'Drafts' && country !== 'MD' && (
                            <>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0 14px', marginBottom: '4px' }}>
                                    <label style={styles.checkboxLabel}>
                                        <input type="checkbox" checked={mathsSearch} onChange={e => setMathsSearch(e.target.checked)} /> Maths
                                    </label>
                                    <label style={styles.checkboxLabel}>
                                        <input type="checkbox" checked={englishSearch} onChange={e => setEnglishSearch(e.target.checked)} /> English
                                    </label>
                                </div>
                                <div style={styles.dbSearch}>
                                    <label style={styles.boldLabel}>Search Lesson in Country</label>
                                    <label style={styles.checkboxLabel}>
                                        <input type="checkbox" checked={chkLessonCode} onChange={e => setChkLessonCode(e.target.checked)} /> Lesson Code
                                    </label>
                                    <label style={styles.checkboxLabel}>
                                        <input type="checkbox" checked={chkLessonName} onChange={e => setChkLessonName(e.target.checked)} /> Lesson Name
                                    </label>
                                    <input
                                        type="text"
                                        style={styles.textBox}
                                        className="lb-textbox"
                                        placeholder="Find lessons..."
                                        value={searchText}
                                        onChange={e => {
                                            setSearchText(e.target.value);
                                            setSelectedDic("");
                                        }}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') handleSearch(e as any);
                                        }}
                                    />
                                    <button style={styles.btn} className="lb-btn" onClick={handleSearch}>Find Lesson</button>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* DIC dropdown — show inline loading text while DIC list is being fetched */}
                {dicsLoading && <Loading />}

                {/* DIC dropdown */}
                {dicPath === 'All lessons' ? (
                    // FIX: "All lessons" uses hardcoded options exactly matching VB:
                    // ALL, Australia, North America (value=Canada)
                    // No "(Australia)" suffix — just the labels as VB shows them
                    <select
                        style={styles.ddl}
                        className="lb-ddl"
                        value={selectedDic}
                        onChange={e => {
                            setSearchText("");
                            setSearchResults([]);

                            setHierarchy([]);
                            setHighlightedLessons(new Set());
                            setHighlightedTests(new Set());

                            setSelectedDic(e.target.value);
                        }}
                    >
                        {ALL_LESSONS_DICS.map(d => (
                            <option key={d.value} value={d.value}>{d.label}</option>
                        ))}
                    </select>
                ) : (
                    <select
                        style={styles.ddl}
                        className="lb-ddl"
                        value={selectedDic}
                        onChange={e => {
                            setSearchText("");          // Clear the Find Lesson textbox
                            setSearchResults([]);       // Clear search results

                            setHierarchy([]);
                            setHighlightedLessons(new Set());
                            setHighlightedTests(new Set());

                            setSelectedDic(e.target.value);
                        }}
                    >
                        <option value="">-- Please Select --</option>
                        {dics.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                )}

                {/* Subject filter — only shown for "All lessons" (mirrors VB pnlSubjectFilter) */}
                {dicPath === 'All lessons' && (
                    <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '0 16px', flexWrap: 'wrap' as const }}>
                        {[
                            { value: 'All', label: 'All' },
                            { value: 'allMath', label: 'Math' },
                            { value: 'allEnglish', label: 'English' },
                            { value: 'allScience', label: 'Science' },
                        ].map(sub => (
                            <label key={sub.value} style={styles.radioLabel}>
                                <input
                                    type="radio" name="subject" value={sub.value}
                                    checked={allLessonSubject === sub.value}
                                    onChange={e => setAllLessonSubject(e.target.value)}
                                    style={{ marginTop: "0" }}
                                />
                                {sub.label}
                            </label>
                        ))}
                    </div>
                )}
            </div>

            {renderTree()}
        </div>
    );
};

export default LessonBrowser;